import { QMenuButton, QMenuItem } from '@qualio/ui-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentVersion } from '../../../../../api/model/document';
import {
  getMostRecentVersion,
  getVersionsToDisplayOrCompare,
  sortVersions,
} from '../../RenderLogic';
import { useFeatureFlagValue } from '../../../../../hooks/useFeatureFlagValue';

type DisplayVersionSelectProps = {
  handleOptionChange: (
    isLatest: boolean,
    docId: number,
    option: string,
  ) => void;
  version: string;
  versions: DocumentVersion[];
  latestVersion: string | null;
  isVersionDisabled?: (version: DocumentVersion) => boolean;
};

export const getVersionsToDisplay = (
  version: string | null | undefined,
  versions: DocumentVersion[],
) => {
  if (versions.length === 0) {
    return [];
  }

  const sortedVersions = sortVersions(versions);
  const latestMajorVersion = sortedVersions[sortedVersions.length - 1].major;

  return getVersionsToDisplayOrCompare(version, latestMajorVersion, versions);
};

export const getVersionsToDocIdMap = (versions: DocumentVersion[]) => {
  return versions.reduce((acc: Record<string, number>, current) => {
    acc[current.version] = current.document_id;
    return acc;
  }, {});
};

export const DisplayVersionSelect = ({
  latestVersion,
  handleOptionChange,
  version,
  versions = [],
  isVersionDisabled,
}: DisplayVersionSelectProps) => {
  const { t } = useTranslation();
  const isNewTemplateUpgradeEnabled = useFeatureFlagValue(
    'new-template-upgrade',
  );
  const sortedVersions = useMemo(() => sortVersions(versions), [versions]);

  const latestLabelVersion = useMemo(
    () =>
      isNewTemplateUpgradeEnabled
        ? getMostRecentVersion(versions)?.version
        : latestVersion,
    [isNewTemplateUpgradeEnabled, versions, latestVersion],
  );

  const label = useMemo(() => {
    if (sortedVersions.length > 1 || isNewTemplateUpgradeEnabled) {
      return version === latestLabelVersion
        ? t('versions.versionLatest', { version })
        : t('versions.version', { version });
    }
    return t('versions.versionLatest', { version });
  }, [
    sortedVersions.length,
    isNewTemplateUpgradeEnabled,
    version,
    latestLabelVersion,
    t,
  ]);

  const versionToDocIdMap = useMemo(
    () => getVersionsToDocIdMap(versions),
    [versions],
  );

  const versionOptions = useMemo(() => {
    const versionsToDisplay = getVersionsToDisplay(version, sortedVersions);

    return sortVersions(versionsToDisplay)
      .reverse()
      .map((version) => {
        return {
          label:
            version.version === latestLabelVersion
              ? t('versions.versionLatest', { version: version.version })
              : t('versions.version', { version: version.version }),
          id: version.version,
          isDisabled: isVersionDisabled?.(version) ?? false,
        };
      });
  }, [sortedVersions, latestLabelVersion, version, isVersionDisabled, t]);

  return (
    <QMenuButton
      isDisabled={versionOptions.length === 0}
      variant="ghost"
      buttonLabel={label}
      data-cy="version-select-button"
    >
      {versionOptions.map((option) => (
        <QMenuItem
          key={option.id}
          data-cy={`version-select-option-${option.id}`}
          isDisabled={option.isDisabled}
          onClick={() => {
            if (versions.length > 1) {
              const isLatest = option.id === latestVersion;
              const docId = versionToDocIdMap[option.id];
              handleOptionChange(isLatest, docId, option.id);
            }
          }}
        >
          {option.label}
        </QMenuItem>
      ))}
    </QMenuButton>
  );
};
